/**
 * <MessagesCard /> — Dashboard tile listing the user's most recent 1-on-1
 * conversations. Hidden when there are no threads.
 *
 * Each row shows the counterpart avatar, name, message preview, unread pill.
 * Clicking a row opens `/messages?to={id}`; the "Open inbox" button navigates
 * to the full inbox.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { MessageCircle, ChevronRight, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Avatar from "@/components/Avatar";

function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function MessagesCard() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    http.get("/messages/threads")
      .then((r) => setThreads(r.data.threads || []))
      .catch(() => setThreads([]))
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return null; // stay silent until we know if there's anything to show
  }
  if (threads.length === 0) return null;

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);
  const preview = threads.slice(0, 3);

  return (
    <div className="wofy-card" data-testid="messages-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>
            <MessageCircle size={12} className="inline -mt-0.5 mr-1" /> MESSAGES
          </div>
          <h2 className="font-display text-xl mt-1">
            Your conversations
            {totalUnread > 0 && (
              <span
                className="ml-2 text-xs rounded-full px-2 py-0.5 text-white align-middle"
                style={{ background: "var(--wofy-primary)" }}
                data-testid="messages-card-unread-badge"
              >
                {totalUnread} unread
              </span>
            )}
          </h2>
        </div>
        <Link to="/messages" className="wofy-btn-ghost text-xs" data-testid="messages-card-open-inbox">
          Open inbox <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="space-y-2" data-testid="messages-card-list">
        {preview.map((t) => {
          const mine = t.last?.from_user_id === user.id;
          const hasUnread = (t.unread || 0) > 0;
          return (
            <Link
              key={t.user.id}
              to={`/messages?to=${t.user.id}`}
              className="flex items-center gap-3 p-2 rounded-lg border hover:bg-black/[0.03] transition"
              style={{ borderColor: "var(--wofy-line)", background: hasUnread ? "rgba(178,93,56,0.05)" : "transparent" }}
              data-testid={`messages-card-thread-${t.user.handle || t.user.id}`}
            >
              <Avatar src={t.user.avatar_url || t.user.profile_picture} name={t.user.name} gender={t.user.gender} size={40} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className={`text-sm truncate ${hasUnread ? "font-bold" : "font-medium"}`}>{t.user.name || "Member"}</div>
                  <time className="text-[10px] font-mono opacity-60 shrink-0">{fmt(t.last?.created_at)}</time>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-xs truncate flex-1" style={{ color: "var(--wofy-text-soft)" }}>
                    {mine && <span className="opacity-60">You: </span>}
                    {t.last?.body}
                  </div>
                  {hasUnread && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full text-white shrink-0" style={{ background: "var(--wofy-primary)" }}>{t.unread}</span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      {threads.length > 3 && (
        <p className="text-xs text-center pt-2 opacity-70">
          {threads.length - 3} more · <Link to="/messages" className="underline">see all</Link>
        </p>
      )}
    </div>
  );
}
